/**
 * =============================================================================
 *  MENU THA XUONG CUA THANH CONG CU - MOT BAN, DUNG CHO CA "TRANG" VA "CHÈN"
 * =============================================================================
 *
 *  BA DIEU KIEN, VA CA BA DEU DA TUNG HONG O DU AN NAY HOAC O NHUNG DU AN GIONG
 *  NO. Chung duoc ghi ra day de lan sua sau khong lam mat lai:
 *
 *  1. MENU PHAI NAM TREN TRANG PDF. Chuyen `.toolbar` sang position:relative +
 *     z-index:20 + overflow:visible la de cho viec nay - xem khoi ghi chu dai
 *     trong styles.css. Menu o day dinh vi tuyet doi trong mot boc
 *     position:relative NAM TRONG thanh cong cu do, nen no thua ke dung ngu
 *     canh xep lop day. KHONG duoc dat overflow khac `visible` len bat ky to
 *     tien nao cua no.
 *
 *  2. NUT MO MENU PHAI CHO THAY BEN TRONG DANG BAT GI. Mot cong cu dang cam ma
 *     khong nhin thay tren thanh cong cu se lam nguoi dung bam len trang de
 *     "chon" mot doi tuong va bat ngo ve ra mot hinh chu nhat. Nen `activeItem`
 *     thay the hoan toan nhan mac dinh cua nut.
 *
 *  3. BAN PHIM PHAI DUNG. Enter/Space/mui-ten-xuong de mo, mui ten de di
 *     chuyen, Escape de dong VA TRA CON TRO VE NUT. Mot menu dong lai roi tha
 *     con tro vao hu khong se nem nguoi dung dung ban phim ve dau tai lieu.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { nextMenuIndex, opensMenu } from './toolbarMenus';

export interface MenuItemSpec {
  key: string;
  label: string;
  icon?: string;
  /** Phim tat hien o mep phai cua muc. Chu HOA, vd "R". */
  shortcut?: string;
  /** Ly do khoa, hoac mo ta thao tac. Luon nen co. */
  title?: string;
  disabled?: boolean;
  /**
   * `undefined` = muc lenh thuong. `boolean` = muc chon mot trong nhieu, va
   * ARIA phai noi dung the: mot cong cu dang bat la `menuitemradio` aria-checked,
   * khong phai mot nut lenh trong giong no tren man hinh.
   */
  checked?: boolean;
  /** Ke mot duong ngan phia tren muc nay. */
  separatorBefore?: boolean;
  onSelect: () => void;
}

export interface DropdownMenuProps {
  /** Nhan mac dinh cua nut, khi khong co muc nao dang bat. */
  label: string;
  icon: string;
  items: readonly MenuItemSpec[];
  disabled?: boolean;
  title?: string;
  /**
   * Muc dang bat ben trong menu. Co mat = nut doi sang nhan va bieu tuong cua
   * no VA sang len. Xem dieu kien (2) o dau tep.
   */
  activeItem?: { label: string; icon: string } | null;
  /** Be rong toi thieu cua bang tha xuong. */
  minWidth?: number;
  /** Ghim mep phai cua bang vao mep phai cua nut. */
  alignEnd?: boolean;
}

export function DropdownMenu({
  label,
  icon,
  items,
  disabled = false,
  title,
  activeItem = null,
  minWidth = 210,
  alignEnd = false,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      setCursor(-1);
      // Tra con tro ve nut. Khong lam thi con tro roi ve <body> va phim Tab ke
      // tiep bat dau lai tu dau trang - nguoi dung dung ban phim mat cho.
      if (returnFocus) triggerRef.current?.focus();
    },
    [],
  );

  // Bam ra ngoai thi dong. Mot menu treo lai tren man hinh sau khi nguoi dung
  // da chuyen su chu y sang trang la mot mang giao dien che mat noi dung.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) close(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [open, close]);

  // Dua con tro vao dung muc dang tro toi. Chay sau moi lan doi `cursor`, ke ca
  // lan mo dau tien - neu khong thi mui ten dau tien khong di chuyen duoc gi.
  useEffect(() => {
    if (!open || cursor < 0) return;
    itemRefs.current[cursor]?.focus();
  }, [open, cursor]);

  const openAt = (index: number) => {
    setOpen(true);
    setCursor(index);
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    if (!open && opensMenu(e.key)) {
      e.preventDefault();
      openAt(0);
      return;
    }
    if (!open && e.key === 'ArrowUp') {
      // ArrowUp mo va nhay xuong muc CUOI - quy uoc quen thuoc, va no rut ngan
      // duong den nhung muc it dung nam cuoi danh sach.
      e.preventDefault();
      openAt(items.length - 1);
      return;
    }
    if (open && e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    // Mo bang CHUOT roi di tiep bang BAN PHIM. Luc do con tro van con o nut,
    // nen neu cho nay khong bat mui ten thi menu dang mo lai khong di chuyen
    // duoc - dung kieu "mo ra roi tac tit" ma nguoi dung se cho la hong.
    if (open) {
      const next = nextMenuIndex(cursor, e.key, items.length);
      if (next !== null) {
        e.preventDefault();
        setCursor(next);
      }
    }
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key === 'Tab') {
      // Tab ra khoi menu = dong menu, nhung KHONG cuop con tro: nguoi dung dang
      // di tiep, keo ho nguoc ve nut la chong lai chinh thao tac ho vua go.
      close(false);
      return;
    }
    const next = nextMenuIndex(cursor, e.key, items.length);
    if (next !== null) {
      e.preventDefault();
      setCursor(next);
    }
  };

  const trigger = activeItem ?? { label, icon };

  return (
    <div className="tbMenu" ref={boxRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`toolButton${activeItem ? ' active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-pressed={activeItem ? true : undefined}
        disabled={disabled}
        title={activeItem ? `${label} — đang bật: ${activeItem.label}` : title ?? label}
        onClick={() => (open ? close(false) : openAt(-1))}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="toolIcon" aria-hidden="true">{trigger.icon}</span>
        <span className="toolLabel">{trigger.label}</span>
        <span className="tbMenuCaret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          className={`tbMenuFlyout${alignEnd ? ' alignEnd' : ''}`}
          id={menuId}
          role="menu"
          aria-label={label}
          style={{ minWidth: `${minWidth}px` }}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item, i) => (
            <div key={item.key} className="tbMenuSlot">
              {item.separatorBefore && <div className="tbMenuSeparator" role="separator" />}
              <button
                type="button"
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
                {...(item.checked === undefined ? {} : { 'aria-checked': item.checked })}
                className={`tbMenuItem${item.checked ? ' active' : ''}`}
                disabled={item.disabled}
                title={item.title}
                tabIndex={-1}
                onClick={() => {
                  // Muc bi khoa khong dong menu: nguoi dung vua bam trung mot
                  // thu khong bam duoc, dong menu di la lay mat luon co hoi doc
                  // ly do trong `title`.
                  if (item.disabled) return;
                  item.onSelect();
                  close(true);
                }}
              >
                <span className="tbMenuIcon" aria-hidden="true">{item.icon ?? ''}</span>
                <span className="tbMenuLabel">{item.label}</span>
                {item.shortcut && <kbd className="tbMenuKey">{item.shortcut}</kbd>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
